import { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';

const RULES = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'One digit', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'One special character', test: (p) => /[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(p) },
];

export function PasswordStrengthMeter({ password = '' }) {
  const [results, setResults] = useState([]);
  const [score, setScore] = useState(0);

  useEffect(() => {
    if (!password) {
      setResults([]);
      setScore(0);
      return;
    }
    const r = RULES.map(rule => ({ ...rule, passed: rule.test(password) }));
    setResults(r);
    const passed = r.filter(x => x.passed).length;
    setScore(Math.round((passed / RULES.length) * 100));
  }, [password]);

  if (!password) return null;

  const color = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const label = score >= 80 ? 'Strong' : score >= 50 ? 'Medium' : 'Weak';

  return (
    <div className="space-y-2" data-testid="password-strength-meter">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">Password strength</span>
        <span className={`font-medium ${score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
          {label}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <div className="space-y-1">
        {results.map(r => (
          <div key={r.key} className="flex items-center gap-1.5 text-xs">
            {r.passed
              ? <Check className="h-3 w-3 text-emerald-500" />
              : <X className="h-3 w-3 text-slate-300" />}
            <span className={r.passed ? 'text-slate-600' : 'text-slate-400'}>{r.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
